import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneTask, completeTask, deleteTask, updateTask, type Task } from '../api';
import { useAccountDay } from '../accountDay';
import { shiftDay } from '../timezones';
import { STATUS_VERB, deleteTaskPrompt } from '../taskVocab';
import type { IconName } from './Icon';
import { MenuSurface, useMenuAnchor, type MenuItem } from './MenuSurface';

/**
 * The right-click menu on a task.
 *
 * Everything here is reachable another way — that is the point. Clone and Delete live at the
 * bottom of the detail panel, which is two clicks and a scroll from a row; "plan for today" means
 * opening the panel and typing into a date field. The menu is a shortcut to the things whose
 * ordinary path is longest, offered where you are already pointing.
 *
 * It is deliberately not the *only* way to reach any of them. The command bar and its keystrokes
 * still do the status verbs, and the detail panel still does clone and delete — so a keyboard-only
 * user loses nothing by this menu existing, which is why it does not implement its own focus
 * trapping and roving tabindex. If it ever becomes the only route to something, that stops being
 * true and this needs the full treatment.
 */

/** Opening state, shared by every surface that shows tasks. */
export function useTaskMenu() {
  const { state, openAt, close } = useMenuAnchor<Task>();
  return { target: state, open: openAt, close };
}

export function TaskMenu({
  target,
  onClose,
  onOpenTask,
  onOpenFull,
  selectedCount = 0,
  onAct,
}: {
  target: { subject: Task; anchor: { x: number; y: number } } | null;
  onClose: () => void;
  onOpenTask: (id: string) => void;
  /** Offered only where a full read view exists to open. */
  onOpenFull?: (id: string) => void;
  /**
   * How many rows are selected, so the menu can say what it is about to act on.
   *
   * Every verb here writes one task. With six rows selected and a seventh right-clicked, "Next
   * week" moved exactly one task and said nothing — so the menu looked like it had acted on the
   * selection and had not. Right-clicking a row outside the selection now clears it (the standard
   * behaviour, and it makes the single-task action unambiguous); right-clicking one *inside* a
   * selection says plainly that this menu is about that row, and points at the control that does
   * act on all of them.
   */
  selectedCount?: number;
  /**
   * The row this menu was opened on is about to be written — see `holdGap` in `useTaskSelection`.
   *
   * Three of the verbs here can take the row out of the list: `Done` and `Drop` on a list that
   * hides them, `Delete` anywhere, and a deferral off the end of a date filter. Announced for every
   * write rather than for those three, because arming stands down on its own if the row stays, and
   * a list of "the verbs that remove a row" would go stale the first time a filter changed.
   */
  onAct?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  /*
    Every date this menu writes is a day in the account's calendar, so all four of them start here.

    "Plan for today" wrote `new Date().toISOString().slice(0, 10)` — the *UTC* day — so at 01:00 in
    Warsaw the most-used verb in this menu planned the task for yesterday, and the item below hid
    itself on the strength of the same wrong string: offered on a task already planned for today,
    withheld on one planned for yesterday. The three deferrals then used a third clock again, the
    browser's, which is right only while the laptop and the profile agree.
  */
  const { today } = useAccountDay();
  const after = (fn?: () => void) => () => {
    void queryClient.invalidateQueries();
    fn?.();
    onClose();
  };

  const setStatus = useMutation({
    onMutate: ({ id }: { id: string; status: Task['status'] }) => onAct?.(id),
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) =>
      status === 'done' ? completeTask(id).then((r) => r.task) : updateTask(id, { status }),
    onSuccess: after(),
  });
  const planToday = useMutation({
    mutationFn: (id: string) => updateTask(id, { scheduledDate: today }),
    onSuccess: after(),
  });
  /*
    Push it out, rather than pull it in.

    Every temporal path led to today or back to the pile: this menu offered "Plan for today" and
    nothing else, and bulk edit had only an absolute date field. Two personas with opposite
    temperaments asked for the same thing — the manager needs thirty things out of the way quickly,
    the planner needs a way out of the overdue pile that is not "do it today".
  */
  const defer = useMutation({
    onMutate: ({ id }: { id: string; days: number }) => onAct?.(id),
    // `shiftDay` on the account's day, not `setDate` on the browser's: whole-day arithmetic is a
    // question about the account's calendar, and `Date.setDate` crosses DST at the wrong moment.
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      updateTask(id, { scheduledDate: shiftDay(today, days) }),
    onSuccess: after(),
  });
  const copy = useMutation({
    mutationFn: (id: string) => cloneTask(id),
    // Straight to the copy: it is in the inbox carrying the original's dates, which is the state
    // you want to look at.
    onSuccess: (created) => after(() => onOpenTask(created.id))(),
  });
  const remove = useMutation({
    onMutate: (id: string) => onAct?.(id),
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: after(),
  });

  if (!target) return null;
  const task = target.subject;

  const items: MenuItem[] = [
    { key: 'open', icon: 'open', label: 'Open', run: () => (onOpenTask(task.id), onClose()) },
    ...(onOpenFull
      ? [
          {
            key: 'open-full',
            icon: 'open' as const,
            label: 'Open full',
            run: () => (onOpenFull(task.id), onClose()),
          },
        ]
      : []),
    { key: 'd1', divider: true },
    // The verb pointing at the state it is already in is absent rather than disabled — the same
    // rule the command bar follows, and the same words: this menu called completion `Done` while
    // the panel two clicks away called it `Complete`, for one call to `completeTask`.
    ...(task.status === 'in_progress' || task.isBlocked
      ? []
      : ([
          {
            key: 'start',
            icon: 'start' as IconName,
            label: STATUS_VERB.in_progress.verb,
            run: () => setStatus.mutate({ id: task.id, status: 'in_progress' }),
          },
        ] as const)),
    ...(task.status === 'done'
      ? []
      : ([
          {
            key: 'done',
            icon: 'done' as IconName,
            label: STATUS_VERB.done.verb,
            run: () => setStatus.mutate({ id: task.id, status: 'done' }),
          },
        ] as const)),
    /* Park sits above Drop because they are the two ways of putting something down and the order
       is the order of commitment: parking decides nothing, dropping decides against. A parked row
       does not offer it again — the same rule every other entry here follows — and the way back is
       "To inbox" or "To next", already in this menu. */
    ...(task.status === 'parked'
      ? []
      : ([
          {
            key: 'park',
            icon: 'park' as IconName,
            label: STATUS_VERB.parked.verb,
            run: () => setStatus.mutate({ id: task.id, status: 'parked' }),
          },
        ] as const)),
    ...(task.status === 'dropped'
      ? []
      : ([
          {
            key: 'drop',
            icon: 'drop' as IconName,
            label: STATUS_VERB.dropped.verb,
            run: () => setStatus.mutate({ id: task.id, status: 'dropped' }),
          },
        ] as const)),
    { key: 'd2', divider: true },
    ...(task.scheduledDate === today
      ? []
      : ([
          {
            key: 'today',
            icon: 'slot' as IconName,
            label: 'Plan for today',
            run: () => planToday.mutate(task.id),
          },
        ] as const)),
    {
      key: 'tomorrow',
      icon: 'slot' as IconName,
      label: 'Tomorrow',
      run: () => defer.mutate({ id: task.id, days: 1 }),
    },
    {
      key: 'next-week',
      icon: 'slot' as IconName,
      label: 'Next week',
      run: () => defer.mutate({ id: task.id, days: 7 }),
    },
    {
      key: 'next-month',
      icon: 'slot' as IconName,
      label: 'Next month',
      run: () => defer.mutate({ id: task.id, days: 30 }),
    },
    { key: 'd2b', divider: true },
    { key: 'clone', icon: 'recur', label: 'Clone', run: () => copy.mutate(task.id) },
    { key: 'd3', divider: true },
    {
      key: 'delete',
      icon: 'trash',
      label: 'Delete',
      danger: true,
      // The same confirmation the detail panel asks for. A right-click is easier to do by
      // accident than a trip to the bottom of a panel, not harder.
      run: () => {
        if (confirm(deleteTaskPrompt(task))) remove.mutate(task.id);
        else onClose();
      },
    },
  ];

  return (
    <MenuSurface
      anchor={target.anchor}
      label={`Actions for ${task.title}`}
      items={items}
      onClose={onClose}
      {...(selectedCount > 1
        ? {
            note: `This menu acts on “${task.title}” only — the command bar acts on all ${selectedCount}.`,
          }
        : {})}
    />
  );
}
